import '@acme/ui/styles.css'

import { createApiClient } from '@acme/api-client'
import { createAuth, installAuthGuard } from '@acme/auth'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { createPinia } from 'pinia'
import { createApp } from 'vue'
import { createPortalApi, PORTAL_API } from '@/api/endpoints'
import { createErpApi, ERP_API } from '@/api/erp/endpoints'
import { createMonitoringApi, MONITORING_API } from '@/api/monitoring/endpoints'
import { CAMPAIGN_API, createCampaignApi } from '@/api/campaign/endpoints'
import { CNPJ_API, createCnpjApi } from '@/api/cnpj/endpoints'
import { createPortalHost, PORTAL_HOST, resolveHostTenant } from '@/features/session/portalHost'
import { createCustomerPortalApi, CUSTOMER_PORTAL_API } from '@/features/customer/customerApi'
import App from './App.vue'
import { restoreCachedTenantTheme, shouldRetry } from '@acme/app-shell'
import { createPortalI18n } from './i18n'
import { createPortalRouter } from './router'
import { loadPortalConfig } from './runtime-config'

async function bootstrap() {
  // The institution's colours from the last visit while the app boots (sign-in callback included).
  restoreCachedTenantTheme()
  const config = await loadPortalConfig()
  const origin = window.location.origin
  // On a tenant address everything stays on this host: the login (/entrar), the OIDC endpoints
  // (same-origin metadata, same issuer), the callback and the page after logout.
  const hostTenant = await resolveHostTenant(config.apiBaseUrl)
  const hostTenantId = hostTenant?.tenantId ?? null

  const auth = createAuth({
    authority: config.authority,
    clientId: config.clientId,
    scopes: config.scopes,
    appUrl: hostTenant ? `${origin}/` : `${origin}${import.meta.env.BASE_URL}`,
    postLogoutUrl: hostTenant ? `${origin}/entrar/saiu?app=assistente` : config.postLogoutUrl,
    sameOriginEndpoints: !!hostTenant,
  })

  const router = createPortalRouter(hostTenantId)
  // A 401 means the session ended: sign in again (the login explains it) and come back.
  const client = createApiClient({
    baseUrl: config.apiBaseUrl,
    getAccessToken: auth.getAccessToken,
    onUnauthorized: () => {
      const route = router.currentRoute.value
      const tenant = route.params.tenantId
      void auth.login(route.fullPath, { expired: true, tenant: typeof tenant === 'string' ? tenant : (hostTenantId ?? undefined) })
    },
  })
  // Same token (it carries the erp-api scope) and the same sign-in on 401.
  const erpClient = createApiClient({
    baseUrl: config.erpApiBaseUrl ?? '/api/erp',
    getAccessToken: auth.getAccessToken,
    onUnauthorized: () => {
      const route = router.currentRoute.value
      const tenant = route.params.tenantId
      void auth.login(route.fullPath, { expired: true, tenant: typeof tenant === 'string' ? tenant : (hostTenantId ?? undefined) })
    },
  })
  // The Monitoring's API: the same token (scope monitoring-api) and the same sign-in on 401.
  const monitoringClient = createApiClient({
    baseUrl: config.monitoringApiBaseUrl ?? '/api/monitoramento',
    getAccessToken: auth.getAccessToken,
    onUnauthorized: () => {
      const route = router.currentRoute.value
      const tenant = route.params.tenantId
      void auth.login(route.fullPath, { expired: true, tenant: typeof tenant === 'string' ? tenant : (hostTenantId ?? undefined) })
    },
  })
  // The CNPJ analysis' API: the same token (scope monitoramento-cnpj-api) and the same sign-in on 401.
  const cnpjClient = createApiClient({
    baseUrl: config.cnpjApiBaseUrl ?? '/api/analise-cnpj',
    getAccessToken: auth.getAccessToken,
    onUnauthorized: () => {
      const route = router.currentRoute.value
      const tenant = route.params.tenantId
      void auth.login(route.fullPath, { expired: true, tenant: typeof tenant === 'string' ? tenant : (hostTenantId ?? undefined) })
    },
  })
  // The Automação de Envios API: the same token (scope campaign-api) and the same sign-in on 401.
  const campaignClient = createApiClient({
    baseUrl: config.campaignApiBaseUrl ?? '/api/envios',
    getAccessToken: auth.getAccessToken,
    onUnauthorized: () => {
      const route = router.currentRoute.value
      const tenant = route.params.tenantId
      void auth.login(route.fullPath, { expired: true, tenant: typeof tenant === 'string' ? tenant : (hostTenantId ?? undefined) })
    },
  })
  installAuthGuard(router, auth, { tenant: hostTenantId ?? undefined })

  createApp(App)
    .use(createPinia())
    .use(createPortalI18n())
    .use(VueQueryPlugin, {
      queryClientConfig: {
        defaultOptions: { queries: { retry: shouldRetry, refetchOnWindowFocus: false, staleTime: 30_000 } },
      },
    })
    .use(auth)
    .provide(PORTAL_API, createPortalApi(client, config.apiBaseUrl))
    .provide(ERP_API, createErpApi(erpClient))
    .provide(MONITORING_API, createMonitoringApi(monitoringClient))
    .provide(CNPJ_API, createCnpjApi(cnpjClient))
    .provide(CAMPAIGN_API, createCampaignApi(campaignClient))
    .provide(PORTAL_HOST, createPortalHost(hostTenantId))
    .provide(CUSTOMER_PORTAL_API, createCustomerPortalApi(config.erpApiBaseUrl ?? '/api/erp', config.apiBaseUrl))
    .use(router)
    .mount('#app')
}

bootstrap().catch((error: unknown) => {
  console.error('The portal could not start', error)
  const root = document.getElementById('app')
  if (root) {
    root.textContent = 'Não foi possível iniciar o portal. Recarregue a página.'
  }
})
